import { SiloArtCanvas } from "@/components/SiloArtLayer";
// iter-32 — Fan Portal (subscriber lockdown view).
//
// This page replaces the Creator Dashboard for users who don't have an active
// platform subscription. It shows their active silo subscriptions, bookmarks,
// tips — the things a fan cares about — plus a single "Become a Creator"
// upsell CTA. No creator tools render here.
//
// Routing logic lives in App.js: /dashboard renders this component when
// user.is_paid_creator === false, and the full Dashboard otherwise.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkle, SignOut, ArrowRight, Books, BookmarkSimple, Crown,
  LockKey, ArrowSquareOut, Heart, DeviceMobile, X, CaretDown, Bell, BellSlash, Eye, CalendarHeart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser, clearAuth } from "@/lib/api";
import { hasPlatformAccess } from "@/lib/access";
import { useViewerSurface } from "@/lib/useViewerSurface";
import GhostReport from "@/components/GhostReport";
import EarningsDisclaimer from "@/components/EarningsDisclaimer";
import PartnerProgramNote from "@/components/PartnerProgramNote";
import CreatorProBuffetModal from "@/components/CreatorProBuffetModal";
import AutoRenewDisclosure from "@/components/AutoRenewDisclosure";
import SiloArt from "@/components/SiloArt";
import IdentityPill from "@/components/IdentityPill";
import BattleInboxBadge from "@/features/battles/BattleInboxBadge";
import { useBattlesEnabled } from "@/features/battles/useBattlesEnabled";
import FanPortalBattlesSection from "@/features/battles/FanPortalBattlesSection";
import FanPortalInteractionsSection from "@/features/interactions/FanPortalInteractionsSection";
import DfyPurchaseCard from "@/components/DfyPurchaseCard";
import DfyReturnHandler from "@/components/DfyReturnHandler";
import { themeLabelFromMap } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";
import PendingConfirmationsModal from "@/components/PendingConfirmationsModal";
import SearchBar from "@/components/SearchBar";
import InvitesBanner from "@/components/InvitesBanner";
import CompAlertBanner from "@/components/CompAlertBanner";
import TrialStartCard from "@/components/TrialStartCard";
import CreatorRestingBanner from "@/components/CreatorRestingBanner";
import TrialStartModal from "@/components/TrialStartModal";
import { useTrial } from "@/lib/trial";
import { getEntitlements } from "@/lib/entitlements";
import InterestsPicker from "@/components/InterestsPicker";

const fmt$ = (cents) => (cents === null || cents === undefined ? "—"
  : `$${((cents || 0) / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`);

// iter-33: sweep localStorage for ghost bookmarks across all silos. Format:
// `ghost_bookmarks__<slug>` → JSON array of {file_title, section_name?, ...}.
// Returns a {slug: [...]} map for POST /api/bookmarks/lift-ghosts.
function collectGhostsFromLocalStorage() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith("ghost_bookmarks__")) continue;
      const slug = k.slice("ghost_bookmarks__".length);
      try {
        const arr = JSON.parse(localStorage.getItem(k) || "[]");
        if (Array.isArray(arr) && arr.length > 0) {
          out[slug] = arr.map((b) => ({
            silo_slug: slug,
            file_title: (b.file_title || "Untitled").slice(0, 200),
            section_name: (b.section_name || "").slice(0, 80),
          }));
        }
      } catch { /* ignore single-key parse error */ }
    }
  } catch { /* ignore */ }
  return out;
}

// iter-33: detect mobile platform for "Add to Home Screen" instructions.
function detectMobilePlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  // iOS detection — covers iPhone, iPad (incl. iPadOS 13+ which reports as Mac)
  const isIOS =
    /iphone|ipad|ipod/.test(ua) ||
    (/macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

export default function FanPortal() {
  const user = getUser();
  const viewer = useViewerSurface();
  const nav = useNavigate();
  const { enabled: battlesEnabled } = useBattlesEnabled();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLockdown, setShowLockdown] = useState(false);
  const [showTrialStart, setShowTrialStart] = useState(false);
  // Oct 2026 (Phase 9) — the welcome modal used to offer the trial directly;
  // Oct 2026 (Randy, pass 3) it now routes to /start, which shows BOTH ways in.
  // `trial.eligible` is still the backend's answer (D34 includes lapsed
  // subscribers) and drives the small trial button under the upgrade pills.
  const { trial: _trialState } = useTrial();
  const trialOffer = (_trialState?.eligible && !_trialState?.live)
    ? { days: _trialState.trial_days || 14 }
    : null;
  // Referenced by the trial button beneath the upgrade pills (TrialStartCard
  // reads the same state); kept as the single source for "is a trial on offer".
  void trialOffer;
  const [showA2HS, setShowA2HS] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  // Twin upgrade pills (Feb 13, 2026): null | "creator" | "affiliate"
  const [upgradeOpen, setUpgradeOpen] = useState(null);
  // Feb 18, 2026 — collapsible Bookmarks + Discover sections (both default
  // closed; click chevron to expand).
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  // Feb 19, 2026 — default OPEN so cold signups (fans with zero
  // subscriptions) immediately see the search bar + the discover grid.
  // Logged-in fans with active subscriptions can collapse it if they want.
  const [discoverOpen, setDiscoverOpen] = useState(true);
  // Feb 2026 (Randy) — Interests picker collapsible + persisted (per-user
  // preference; the picker itself is a cross-silo affordance so it lives
  // outside any single silo's prefs).
  const [interestsOpen, setInterestsOpen] = useState(() => {
    try { const v = localStorage.getItem("mysilo_interests_open"); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("mysilo_interests_open", interestsOpen ? "1" : "0"); } catch { /* ignore */ }
  }, [interestsOpen]);
  // Oct 2026 (Randy) — "Show only Defined interests" for the Discover search.
  // Off until he flips it, then remembered; `interestCount` comes from the
  // picker above so the switch can explain itself when there is nothing to
  // filter by yet.
  const [interestCount, setInterestCount] = useState(0);
  const [interestsOnly, setInterestsOnly] = useState(() => {
    try { return localStorage.getItem("mysilo_discover_interests_only") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("mysilo_discover_interests_only", interestsOnly ? "1" : "0"); } catch { /* ignore */ }
  }, [interestsOnly]);
  // The count has to stand on its own: the picker only mounts while that
  // section is expanded, and the switch must still explain itself when it is
  // collapsed. The picker refreshes this number after a save.
  useEffect(() => {
    api.get("/me/interests")
      .then((r) => setInterestCount((r.data?.interests || []).length))
      .catch(() => { /* not fatal — the switch just stays disabled */ });
  }, []);
  // Pricing pulled live from /public/pricing (Feb 2026), with
  // /me/affiliate-status as the authenticated fallback. Oct 3 2026 (Randy,
  // stale-pricing cleanup) — the creator price used to default to 1000, which
  // kept rendering the retired $10 whenever the fetch was slow or failed. It
  // starts as null now and `fmt$(null)` renders "—": an unknown price looks
  // unknown instead of looking like an old one.
  //
  // Oct 3 2026, SECOND PASS — the gate remembered `30000` ($300) here too,
  // and the live gate is `33300` ($333), derived from the Creator Pro
  // catalog's bundle.day1_cents. So it was quoting a fan a price $33 below
  // what Stripe would charge. Nulled, like every other money field.
  // `rev_share_pct` was also being stored and read by exactly nothing;
  // removed rather than nulled.
  const [pricing, setPricing] = useState({
    creator_monthly_fee_cents: null,
    day_one_bundle_discount_threshold_cents: null,
  });
  useEffect(() => {
    (async () => {
      try {
        // Feb 2026 (Randy pt-fanportal-pricing) — /me/affiliate-status
        // required auth so anon fans got a hardcoded fallback, which meant
        // sliders in Billing & Earnings didn't reach them. Now we hit
        // /public/pricing first (anon-friendly), and only fall back to
        // /me/affiliate-status if it also has the field.
        const r = await api.get("/public/pricing");
        setPricing({
          creator_monthly_fee_cents: r.data?.creator_monthly_fee_cents
            ?? r.data?.platform_fee_cents ?? null,
          day_one_bundle_discount_threshold_cents:
            r.data?.day_one_bundle_discount_threshold_cents ?? null,
          creator_tier_silo_cap: r.data?.creator_tier_silo_cap || 3,
          creator_pro_tier_silo_cap: r.data?.creator_pro_tier_silo_cap || 12,
        });
      } catch {
        // Fail open with the defaults — copy stays close enough for non-logged states.
      }
    })();
  }, []);

  // Phase A (Jul 2026, Randy) — Family Access Point. Personal silos this user
  // was invited to (collaborator, not owner) surface as "Your family" cards
  // linking to the focused Family view (capture pad + calendar, no dashboard).
  const [familySilos, setFamilySilos] = useState([]);
  // Feb 2026 pt89h (Randy) — Randy's wife (parent-pill collaborator) needs
  // to see chore-approval alerts from the Fan Portal since she has no
  // Creator Dashboard access. Poll a batch summary and show a tiny dot
  // on each family card that has pending items for HER (parent-actor).
  const [familyPendingCounts, setFamilyPendingCounts] = useState({});
  const [familyPendingOpen, setFamilyPendingOpen] = useState(null);
  useEffect(() => {
    if (familySilos.length === 0) return;
    let mounted = true;
    const fetchSummary = async () => {
      try {
        const r = await api.get("/calendar/pending-confirmations-summary");
        if (mounted) setFamilyPendingCounts(r.data?.summary || {});
      } catch { if (mounted) setFamilyPendingCounts({}); }
    };
    fetchSummary();
    const t = setInterval(fetchSummary, 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, [familySilos.length]);
  // Feb 2026 pt89h (Randy) — deleted the racing /silos useEffect that
  // filtered to silo_kind==='personal' and clobbered organizer silos
  // populated by /fan/portal. `/fan/portal` already returns family_silos
  // (both personal AND organizer). The old effect only existed before
  // /fan/portal wired family_silos in (pt89c).

  useEffect(() => {
    let cancel = false;
    // Jun 2026 (Randy) — his son was comped while sitting on this page and the
    // account never transitioned. The cached session user is a snapshot taken
    // at login, so a comp granted mid-session stays invisible until something
    // re-reads the server. `/fan/portal` returns a fresh user, so compare the
    // BEFORE snapshot against it: the moment the server says this account has
    // creator access, reload once so App.js's DashboardGate re-runs and lands
    // on the Creator Dashboard. One-shot by construction — after the reload
    // this component no longer mounts.
    const hadCreatorAccess = hasPlatformAccess(getUser());

    (async () => {
      try {
        // iter-33: lift-ghosts FIRST (idempotent server-side) so the portal
        // load reflects them. Skip if no ghosts in localStorage.
        const ghosts = collectGhostsFromLocalStorage();
        if (Object.keys(ghosts).length > 0) {
          try {
            await api.post("/bookmarks/lift-ghosts", { ghosts_by_silo: ghosts });
            // Clear localStorage ghosts AFTER successful server lift. If it
            // fails we leave them in place to retry next visit.
            for (const slug of Object.keys(ghosts)) {
              try { localStorage.removeItem(`ghost_bookmarks__${slug}`); } catch { /* ignore */ }
            }
          } catch { /* swallow — non-fatal, user can re-trigger by reloading */ }
        }

        const r = await api.get("/fan/portal");
        if (cancel) return;
        setData(r.data);
        if (r.data?.user) setUser(r.data.user);
        const fresh = r.data?.user;
        const hasCreatorAccess = hasPlatformAccess(fresh);
        if (hasCreatorAccess && !hadCreatorAccess) {
          window.location.reload();
          return;
        }
        // Feb 2026 (Randy pt89c) — family_silos wire-in. Before this,
        // family invitees had NO path from FanPortal to their shared
        // Organizer silo's calendar / capture pad — the "Your family"
        // section rendered nothing because familySilos was never set.
        setFamilySilos(r.data?.family_silos || []);

        // iter-40f — Throttle the lockdown + A2HS popups so they don't
        // fire on EVERY fan portal visit. Counter-based (show 1-in-5).
        // Counter lives in localStorage so it survives tab closes too,
        // not just intra-session.
        const WELCOME_KEY = "fan_welcome_counter";
        const A2HS_KEY = "fan_a2hs_counter";
        const a2hsDismissed = localStorage.getItem("mysilo_a2hs_dismissed");
        const incr = (key) => {
          const n = parseInt(localStorage.getItem(key) || "0", 10) + 1;
          localStorage.setItem(key, String(n));
          return n;
        };
        const welcomeN = incr(WELCOME_KEY);
        // Show on visit #1 (first time ever), then every 5th visit.
        // Phase-A — but never to operators (creators / affiliate creators /
        // super admins): they don't need the "Unlock Creator tools" pitch.
        // Oct 2026 (TRIAL_PLAN.md §8.10) — and never to a creator whose
        // subscription lapsed. They're parked here because their dashboard
        // closed, not because they're a new fan weighing up creator tools;
        // "build your own Digital AI Twin" is wrong for someone whose twin
        // already exists and is resting. The resting banner speaks instead.
        const darkNow = await getEntitlements().then((e) => !!e?.dark?.dark).catch(() => false);
        if (!viewer.isOperator && !darkNow && (welcomeN === 1 || welcomeN % 5 === 0)) {
          setShowLockdown(true);
        } else {
          // If we didn't show the welcome, independently consider A2HS.
          const a2hsN = incr(A2HS_KEY);
          if (!a2hsDismissed && detectMobilePlatform() !== "desktop" && (a2hsN === 2 || a2hsN % 5 === 0)) {
            setShowA2HS(true);
          }
        }
      } catch (e) {
        if (e.response?.status === 401) { clearAuth(); nav("/login"); return; }
        toast.error("Failed to load your subscriptions.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // viewer.isOperator only flips when the cached user object changes; we
    // intentionally read it once at mount so the popup decision is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const logout = () => { clearAuth(); nav("/login"); };

  // Feb 21, 2026 — reload subs after fan-side unsubscribe so the card
  // disappears without a hard refresh.
  const reloadPortal = async () => {
    try {
      const r = await api.get("/fan/portal");
      setData(r.data);
    } catch { /* ignore — UI keeps last-good state */ }
  };

  const unsubscribeFromSilo = async (slug, siloName, priceCents) => {
    // Sonner-based confirm — window.confirm() silently fails in the
    // hosting preview iframe. Toast stays sticky until the user picks.
    const id = toast(
      `Unsubscribe from ${siloName}?`,
      {
        description: priceCents > 0
          ? "Your monthly charges stop immediately. You can re-subscribe any time."
          : "You'll come off the follower list. You can re-follow any time.",
        duration: 12000,
        action: {
          label: "Unsubscribe",
          onClick: async () => {
            try {
              await api.post(`/silos/${slug}/unfollow`, {});
              toast.success(`Unsubscribed from ${siloName}.`);
              await reloadPortal();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Could not unsubscribe.");
            }
          },
        },
        cancel: {
          label: "Keep",
          onClick: () => toast.dismiss(id),
        },
      },
    );
  };

  // iter-40f — When lockdown closes, DON'T chain the A2HS popup immediately.
  // The counter-based throttle above already decides when A2HS shows on its
  // own visit cadence. Daisy-chaining them felt spammy.
  const closeLockdownAndMaybeShowA2HS = () => {
    setShowLockdown(false);
  };

  const dismissA2HS = (forever = false) => {
    setShowA2HS(false);
    if (forever) {
      try { localStorage.setItem("mysilo_a2hs_dismissed", "1"); } catch { /* ignore */ }
    }
  };

  const upgradeToCreator = async () => {
    if (upgrading) return;
    setUpgrading(true);
    try {
      // iter-33: when upgrading from FanPortal, pass the user's most recent
      // silo as the upgrade source. If they navigated here via a specific
      // silo (subscriptions[0]), credit goes to THAT creator.
      const upgrade_source_silo_slug = (data?.subscriptions || [])[0]?.silo_slug;
      const r = await api.post("/stripe/platform/checkout", {
        origin_url: window.location.origin,
        return_path: "/fan-portal",
        ...(upgrade_source_silo_slug ? { upgrade_source_silo_slug } : {}),
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setUpgrading(false);
    }
  };

  const subs = data?.subscriptions || [];
  const bookmarks = data?.bookmarks || [];
  const lockedBookmarks = data?.locked_bookmarks || [];
  // Group bookmarks by silo_slug
  const bookmarksBySilo = bookmarks.reduce((acc, b) => {
    const key = b.silo_slug || "__no_silo__";
    (acc[key] = acc[key] || []).push(b);
    return acc;
  }, {});
  // Group locked bookmarks by silo_slug too
  const lockedBySilo = lockedBookmarks.reduce((acc, b) => {
    const key = b.silo_slug || "__no_silo__";
    (acc[key] = acc[key] || []).push(b);
    return acc;
  }, {});
  // Union of silos that have either real or locked bookmarks
  const allBookmarkSlugs = Array.from(new Set([
    ...Object.keys(bookmarksBySilo),
    ...Object.keys(lockedBySilo),
  ]));
  // Feb 21, 2026 — show the SILO name in the welcome lockdown, not the
  // creator's first name. Fans subscribe to a SILO ("You're subscribed to
  // Cosmic Equivalence"), not to a person.
  const featuredCreator = subs[0]?.silo_name || subs[0]?.creator_name;
  const featuredSilo = subs[0];
  const platformInstall = detectMobilePlatform();

  return (
    <div className="min-h-screen bg-[#FDFCF8] relative isolate" data-testid="fan-portal-page">
      <SiloArtCanvas />
      <DfyReturnHandler />
      {/* Jun 2026 — a comp with dues REQUIRED keeps the account in the Fan
          Portal by design, and the dues alert used to render only inside the
          Creator Dashboard, i.e. nowhere this user could see it. */}
      <CompAlertBanner />
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="fan-portal-home">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center">
              <Sparkle size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-serif-display text-xl leading-none">My Silo</div>
              <div className="text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1">Fan portal</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            {/* Sprint Feb 8, 2026 — email removed from header per user request.
                The Identity Pill in the upper-right already shows who you are. */}
            {/* Silo Wars spectator entry — gated by master toggle. Jul 2026
                (Randy): hidden on mobile (the lone sword icon next to the
                identity pill was clutter); still available on desktop. */}
            {battlesEnabled && (
              <Link to="/wars" data-testid="fan-portal-watch-wars"
                className="hidden sm:flex text-sm px-3 py-2 rounded-md text-[#A89360] hover:bg-[#1C1B1A]/5 transition-colors items-center gap-1.5">
                <span className="text-base leading-none">⚔</span> <span>Silo Wars</span>
              </Link>
            )}
            {/* Jul 2026 (Randy) — Sign out moved into the identity dropdown. */}
            <BattleInboxBadge />
            <IdentityPill />
          </div>
        </div>
      </header>

      {/* Iter160n (Randy pt-siloart-loosetext-categorical, Feb 2026) — every
          themed page's <main> gets `.siloart-copy` so the loose-text rule
          in index.css catches its headings + spans automatically. Auth /
          legal pages that never render V2 art get it too (harmless no-op
          when `body.siloart-v2` isn't active). Codified as a platform-wide
          pattern in the CSS comment block. */}
      <main className="siloart-copy max-w-5xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1C1B1A]">
            Welcome, <em className="text-[#4A5D4E]">{user?.name?.split(" ")[0] || "friend"}</em>.
          </h1>
          <p className="mt-2 text-[#6B6A68] max-w-2xl">
            Your fan dashboard — every silo you've subscribed to, every conversation you've bookmarked, every creator you've supported. All in one place.
          </p>
        </motion.div>

        {/* Collaboration invites (Phase B) — fans previously never saw these. */}
        <div className="mt-6"><InvitesBanner /></div>

        {/* Oct 2026 (TRIAL_PLAN.md §8.10) — a lapsed creator is routed here, so
            the resting banner lives where they actually land. It sits ABOVE the
            upgrade pills on purpose: explain the rest first, then offer the way
            back. (Oct 2026, fan-first pass: it self-hides for anyone who has
            never held access — see role_helpers.had_creator_access. Oct 2026,
            Randy: it is now a closed chevron row that also requires
            `silo_count > 0`, because a comp or a used trial counted as "held
            access" and fans owning nothing were being told their silos were
            resting.) */}
        <CreatorRestingBanner />

        {/* Sprint Feb 13, 2026 — Twin upgrade pills at the top of the fan portal.
            May 11, 2026: visibility gated by user role so each tier only sees
            the upgrades that actually apply to them:
              - Pure fan:    BOTH pills visible
              - Creator:     "Become a Creator" hidden, only Affiliate pill shows
              - Affiliate:   BOTH pills hidden (nothing left to upgrade to)
            Feb 2026 (Randy) — aesthetic polish. Same size; upgraded to a
            professional two-line pill: eyebrow row (icon + "For creators"
            / "Pro tier"), headline, and a right-side "→" arrow chip. Look
            mirrors the (now-removed) bottom Become-a-Creator card, so both
            top pills feel like premium marketing surfaces. Creator Pro
            keeps its distinct gold accent so the two are visually distinct.
        */}
        {(() => {
          const isSuper = user?.role === "super_admin";
          const isPaidCreator = !!user?.is_paid_creator || isSuper;
          const isAffiliate = !!user?.is_affiliate || isSuper;
          const showCreatorPill = !isPaidCreator && !isAffiliate;
          const showAffiliatePill = !isAffiliate;
          if (!showCreatorPill && !showAffiliatePill) return null;
          const gridCols = showCreatorPill && showAffiliatePill ? "sm:grid-cols-2" : "sm:grid-cols-1";
          return (
            <div className={`grid grid-cols-1 ${gridCols} gap-3 mt-8`} data-testid="fan-portal-upgrade-pills">
              {showCreatorPill && (
                <button
                  type="button"
                  onClick={() => setUpgradeOpen("creator")}
                  data-testid="fan-portal-become-creator-pill"
                  className="relative overflow-hidden bg-gradient-to-br from-[#1C1B1A] to-[#3A4A3E] text-[#FDFCF8] rounded-md px-5 py-3.5 text-left group hover:shadow-lg transition-shadow"
                >
                  <div className="absolute top-1.5 right-1.5 opacity-15"><Crown size={36} weight="duotone" /></div>
                  <div className="relative z-10">
                    <div className="text-[8px] uppercase tracking-[0.28em] text-[#C16E5A] mb-1 flex items-center gap-1.5">
                      <Sparkle size={9} weight="duotone" /> For creators
                    </div>
                    <div className="font-serif-display text-sm leading-tight text-white flex items-center justify-between gap-2">
                      <span>Become a <em className="text-[#C16E5A] not-italic font-semibold">Creator</em></span>
                      <span className="text-[10px] text-white/70 group-hover:text-white flex items-center gap-1 shrink-0">
                        Run your own silos <ArrowRight size={11} weight="bold" />
                      </span>
                    </div>
                  </div>
                </button>
              )}
              {showAffiliatePill && (
                <button
                  type="button"
                  onClick={() => setUpgradeOpen("affiliate")}
                  data-testid="fan-portal-become-affiliate-pill"
                  className="relative overflow-hidden bg-gradient-to-br from-[#3A2F0F] to-[#7A6338] text-[#FDFCF8] rounded-md px-5 py-3.5 text-left group hover:shadow-lg transition-shadow"
                >
                  <div className="absolute top-1.5 right-1.5 opacity-15"><Sparkle size={36} weight="duotone" /></div>
                  <div className="relative z-10">
                    <div className="text-[8px] uppercase tracking-[0.28em] text-[#F5D68C] mb-1 flex items-center gap-1.5">
                      <Crown size={9} weight="duotone" /> Pro tier
                    </div>
                    <div className="font-serif-display text-sm leading-tight text-white flex items-center justify-between gap-2">
                      <span>Unlock <em className="text-[#F5D68C] not-italic font-semibold">Creator Pro</em></span>
                      <span className="text-[10px] text-white/70 group-hover:text-white flex items-center gap-1 shrink-0">
                        The full toolkit <ArrowRight size={11} weight="bold" />
                      </span>
                    </div>
                  </div>
                </button>
              )}
            </div>
          );
        })()}

        {/* Oct 2026 (TRIAL_PLAN.md §8.2b + Randy's fan-first pass) — the trial
            offer lives here because every unpaid account is routed to the Fan
            Portal and never sees the dashboard it unlocks. It is deliberately
            the SMALLEST thing in this stack: one button under the two upgrade
            cards, because a trial is a third option and not the headline. The
            terms open when it's tapped. */}
        <TrialStartCard variant="mini" />

        {/* Silo Wars upcoming/live section. Self-hides if master toggle
            is off OR there's nothing live or scheduled. */}
        <FanPortalBattlesSection />

        {/* Sprint Feb 7, 2026 — moved upsell ads to the bottom of the page
            (operator-aware too). Top of the portal now leads with the
            user's actual content (Ghost Report → stats → subs → bookmarks
            → Discover) and the two ads sit just above the footer at 50%
            footprint. */}

        {/* Phase-2 PI: Ghost Report teaser — drives the SaaS → Operator →
            Partner pipeline by showing fans what they'd already be earning
            if they'd been an Affiliate when their friends signed up.
            Nov 2026 (Randy) — the card described a share link the fan had no
            way to obtain: `shareUrl` was never passed. The button inside it
            now hands it over. */}
        <div className="mt-10" data-testid="fan-portal-ghost-teaser">
          <GhostReport variant="fan" showShareButton />
        </div>

        {/* Lifetime stats strip — Feb 21, 2026 compacted to a single
            horizontal row of inline pills. Same 3 metrics, same data,
            ~60% smaller footprint. Stays 3-up at every breakpoint so
            mobile gets a tight horizontal strip instead of stacked
            full-width cards. */}
        <div className="flex items-stretch gap-2 mt-6 overflow-x-auto" data-testid="fan-stats-strip">
          <div className="flex-1 min-w-0 bg-white border hairline rounded-lg px-3 py-2.5 flex items-center gap-2.5">
            <Books size={16} weight="duotone" className="text-[#4A5D4E] shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] leading-tight">Subs</div>
              <div className="font-serif-display text-base tabular-nums text-[#1C1B1A] leading-tight" data-testid="stat-subs-count">{subs.length}</div>
            </div>
          </div>
          <div className="flex-1 min-w-0 bg-white border hairline rounded-lg px-3 py-2.5 flex items-center gap-2.5">
            <BookmarkSimple size={16} weight="duotone" className="text-[#4A5D4E] shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] leading-tight">Bookmarks</div>
              <div className="font-serif-display text-base tabular-nums text-[#1C1B1A] leading-tight" data-testid="stat-bookmarks-count">{bookmarks.length}</div>
            </div>
          </div>
          <div className="flex-1 min-w-0 bg-white border hairline rounded-lg px-3 py-2.5 flex items-center gap-2.5">
            <Heart size={16} weight="duotone" className="text-[#C16E5A] shrink-0" />
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] leading-tight">Tips sent</div>
              <div className="font-serif-display text-base tabular-nums text-[#1C1B1A] leading-tight" data-testid="stat-tips-total">{fmt$(data?.total_tipped_cents || 0)}</div>
            </div>
          </div>
        </div>

        {/* Family silos (Phase A) — personal silos this user was invited to.
            The special access point Randy specified: family members reach the
            shared organizer WITHOUT ever seeing the creator dashboard. */}
        {familySilos.length > 0 && (
          <section className="mt-10" data-testid="fan-family-section">
            <h2 className="font-serif-display text-2xl text-[#1C1B1A]">
              {/* Feb 19, 2026 (Randy Tactic A) — header label reflects whether
                  ANY of the collab silos are actually family-shaped. If none
                  are (all team-shaped organizers), reads "Your teams". If a
                  mix, reads "Your family & teams". */}
              {(() => {
                const hasFamily = familySilos.some((s) => s.is_family_shaped || s.silo_kind === "personal");
                const hasTeam = familySilos.some((s) => s.silo_kind === "organizer" && !s.is_family_shaped);
                if (hasFamily && hasTeam) return "Your family & teams";
                if (hasTeam && !hasFamily) return "Your teams";
                return "Your family";
              })()}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {familySilos.map((s) => {
                const isFamilyShaped = s.is_family_shaped || s.silo_kind === "personal";
                const openPath = isFamilyShaped ? `/family-view/${s.silo_id}` : `/dashboard/silo/${s.silo_id}`;
                // pt89U Stage-C — per-card theme_map lookup. Each silo in the
                // list belongs to a different owner, so we can't share a
                // React provider — use the plain-function helper instead.
                const cardBlurb = themeLabelFromMap(
                  s.theme_map,
                  isFamilyShaped ? T.THEME_KEY_FANPORTAL_CARD_FAMILY_BLURB : T.THEME_KEY_FANPORTAL_CARD_TEAM_BLURB,
                );
                const cardCta = themeLabelFromMap(
                  s.theme_map,
                  isFamilyShaped ? T.THEME_KEY_FANPORTAL_CARD_FAMILY_CTA : T.THEME_KEY_FANPORTAL_CARD_TEAM_CTA,
                );
                return (
                <div key={s.silo_id} className="relative bg-white border-2 border-[#DCE5DD] rounded-xl p-5 flex items-start gap-3"
                  data-testid={`family-card-${s.silo_slug || s.silo_id}`}>
                  {/* Pending-confirmations dot only for family-shaped silos.
                      Team-shaped organizer silos never generate chore approvals. */}
                  {isFamilyShaped && (familyPendingCounts[s.silo_id] || 0) > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setFamilyPendingOpen({ id: s.silo_id, name: s.silo_name }); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onPointerUp={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseUp={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                      data-testid={`family-pending-dot-${s.silo_slug || s.silo_id}`}
                      title={`${familyPendingCounts[s.silo_id]} chore${familyPendingCounts[s.silo_id] === 1 ? "" : "s"} awaiting your OK`}
                      className="absolute top-2 right-2 z-40 w-2.5 h-2.5 rounded-full bg-[#E86A5C] ring-2 ring-white/70 shadow-[0_1px_3px_rgba(0,0,0,0.35)] hover:scale-125 transition-transform" />
                  )}
                  <div className="w-10 h-10 rounded-full bg-[#4A5D4E] text-[#FDFCF8] flex items-center justify-center shrink-0">
                    <CalendarHeart size={20} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif-display text-lg text-[#1C1B1A] truncate">{s.silo_name}</div>
                    <p className="text-xs text-[#6B6A68] mt-0.5 leading-relaxed">
                      {cardBlurb} Owned by <strong>{s.owner_name}</strong>.
                    </p>
                    <Link to={openPath}
                      data-testid={`family-open-${s.silo_slug || s.silo_id}`}
                      className="inline-flex items-center gap-1.5 mt-3 text-sm px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E]">
                      {cardCta} <ArrowRight size={13} weight="bold" />
                    </Link>
                  </div>
                </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Active subscriptions */}
        <section className="mt-10" data-testid="fan-subscriptions-section">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Your active subscriptions</h2>
            {subs.length > 3 && (
              <span className="text-[10px] text-[#6B6A68]">{subs.length} silos · scroll for more</span>
            )}
          </div>
          {loading ? (
            <div className="text-[#6B6A68] text-sm mt-4">Loading…</div>
          ) : subs.length === 0 ? (
            <div className="bg-white border-2 border-dashed hairline rounded-xl p-10 text-center mt-4" data-testid="subs-empty">
              <Books size={32} weight="duotone" className="mx-auto text-[#4A5D4E]" />
              <p className="font-serif-display text-lg mt-3 text-[#1C1B1A]">No subscriptions yet.</p>
              <p className="text-sm text-[#6B6A68] mt-1">Discover creators by opening any public silo link.</p>
            </div>
          ) : (
            /* Feb 21, 2026 — single-column layout (was 2-col) so cards
               stay compact and same-width regardless of how many silos
               the fan follows. Cap to 3 visible (~120px each) then scroll;
               prevents the section from dominating the page when a fan
               follows 20+ creators. Removed "by Randy" subline (noise),
               muted the FREE-TIER pill to match background, shrank the
               "free access" callout + dropped the party emoji. */
            <div className={`mt-4 space-y-3 ${subs.length > 3 ? "max-h-[420px] overflow-y-auto pr-1" : ""}`}>
              {subs.map((s) => (
                <Link key={s.silo_id} to={`/s/${s.silo_slug}`}
                  className="bg-white border hairline rounded-xl p-4 relative block hover:border-[#4A5D4E] hover:shadow-sm transition-colors"
                  data-testid={`sub-card-${s.silo_slug}`}>
                  {s.silo_art_enabled && s.cube?.faces?.center?.image_id && (
                    <SiloArt cube={s.cube} enabled className="w-24 h-24 mb-3" data-testid={`sub-art-${s.silo_slug}`} />
                  )}
                  {/* Tier badge — muted, smaller, top-right */}
                  <span className={`absolute top-3 right-3 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${
                    s.tier === "paying_subscriber" ? "bg-[#4A5D4E]/10 text-[#4A5D4E]" :
                    s.tier === "paying_subscriber_free" ? "bg-[#F0F1EE] text-[#6B6A68]" :
                    "bg-[#F0F1EE] text-[#6B6A68]"
                  }`} data-testid={`sub-tier-${s.silo_slug}`}>
                    {s.tier_label}
                  </span>
                  <h3 className="font-serif-display text-lg pr-24 leading-tight text-[#1C1B1A]">{s.silo_name}</h3>
                  {s.silo_tagline && <p className="text-[11px] text-[#6B6A68] mt-1 line-clamp-1">{s.silo_tagline}</p>}
                  {s.tier === "paying_subscriber_free" && (
                    <p className="text-[10px] text-[#6B6A68] mt-2 leading-relaxed" data-testid={`sub-free-callout-${s.silo_slug}`}>
                      Full access free — tipping welcome.
                    </p>
                  )}
                  {s.tier === "non_paying_subscriber" && s.price_cents > 0 && (
                    <p className="text-[10px] text-[#6B6A68] mt-2 leading-relaxed" data-testid={`sub-paywall-callout-${s.silo_slug}`}>
                      Following · Unlock unlimited questions for {fmt$(s.supporter_total_cents || s.price_cents)}/mo
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] inline-flex items-center gap-1.5"
                      data-testid={`sub-open-${s.silo_slug}`}>
                      Open silo <ArrowSquareOut size={11} />
                    </span>
                    {s.price_cents > 0 && (
                      /* Sep 25, 2026 (F5) — quote what the card is actually
                         charged, which is what the renewal email already says.
                         The split is on the tooltip; grandfathered rows carry
                         no fee and fall back to the creator's price. */
                      <span className="text-[10px] text-[#6B6A68]" data-testid={`sub-price-${s.silo_slug}`}
                        title={s.supporter_fee_cents
                          ? `${fmt$(s.price_cents)} to the creator + ${fmt$(s.supporter_fee_cents)} My Silo supporter service fee`
                          : undefined}>
                        {fmt$(s.supporter_total_cents || s.price_cents)}/mo
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-3">
                      <SiloPingToggle siloId={s.silo_id} siloSlug={s.silo_slug} />
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); unsubscribeFromSilo(s.silo_slug, s.silo_name, s.price_cents); }}
                        className="text-[10px] text-[#6B6A68] hover:text-[#C16E5A] underline-offset-2 hover:underline"
                        data-testid={`sub-unsubscribe-${s.silo_slug}`}
                      >
                        Unsubscribe
                      </button>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Your interactions — Feb 17, 2026.
            Silos you starred or asked questions on, but didn't subscribe to.
            Subscriber rows already live in the section above. */}
        <FanPortalInteractionsSection />

        {/* Bookmarks — real + locked carryover from ghost lift. Collapsed
            by default — shows count + latest title; chevron expands the full
            per-silo list. (Feb 18, 2026 — UI declutter sweep.) */}
        {(bookmarks.length > 0 || lockedBookmarks.length > 0) && (
          <section className="mt-12" data-testid="fan-bookmarks-section">
            <button
              type="button"
              onClick={() => setBookmarksOpen((v) => !v)}
              data-testid="fan-bookmarks-toggle"
              className="w-full flex items-center justify-between gap-3 text-left group"
              aria-expanded={bookmarksOpen}
            >
              <div className="min-w-0">
                {/* Iter160o (Randy pt-siloart-loosetext-sweep-2) — H2 + subtitle
                    live inside a link-styled button. `.silo-loose` opts them
                    into the whitetext override so they don't stay dark on
                    dark art. */}
                <h2 className="font-serif-display text-2xl text-[#1C1B1A] silo-loose">
                  Your bookmarks <span className="text-[#6B6A68] text-base silo-loose">({bookmarks.length + lockedBookmarks.length})</span>
                </h2>
                {!bookmarksOpen && (bookmarks[0] || lockedBookmarks[0]) && (
                  <p className="text-xs text-[#6B6A68] mt-1 truncate silo-loose" data-testid="fan-bookmarks-latest">
                    Latest: <span className="text-[#1C1B1A] silo-loose">{(bookmarks[0] || lockedBookmarks[0])?.file_title || "Untitled"}</span>
                  </p>
                )}
              </div>
              <CaretDown
                size={18}
                weight="duotone"
                className={`text-[#6B6A68] shrink-0 transition-transform ${bookmarksOpen ? "rotate-180" : ""} group-hover:text-[#1C1B1A]`}
              />
            </button>
            {bookmarksOpen && lockedBookmarks.length > 0 && (
              <p className="text-xs text-[#6B6A68] mt-2">
                <LockKey size={10} weight="duotone" className="inline" /> Locked bookmarks unlock when you upgrade to Creator.
              </p>
            )}
            {bookmarksOpen && (
            <div className="mt-4 space-y-5">
              {allBookmarkSlugs.map((slug) => {
                const reals = bookmarksBySilo[slug] || [];
                const locks = lockedBySilo[slug] || [];
                const groupName = locks[0]?.silo_name || slug;
                return (
                  <div key={slug} className="bg-white border hairline rounded-xl p-5" data-testid={`bookmark-group-${slug}`}>
                    <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-3 flex items-center justify-between">
                      <span>{slug === "__no_silo__" ? "Other" : `/s/${slug}`}</span>
                      {slug !== "__no_silo__" && (
                        <Link to={`/s/${slug}`} className="text-[10px] text-[#4A5D4E] hover:text-[#1C1B1A] normal-case tracking-normal">Open silo →</Link>
                      )}
                    </div>
                    <ul className="space-y-1.5">
                      {reals.map((b) => (
                        <li key={b.id} className="text-sm text-[#1C1B1A] flex items-center gap-2" data-testid={`bookmark-real-${b.id}`}>
                          <BookmarkSimple size={12} weight="duotone" className="text-[#C16E5A] shrink-0" />
                          <span className="truncate">{b.file_title || "Untitled"}</span>
                          {b.section_name && <span className="text-[10px] text-[#6B6A68]">· {b.section_name}</span>}
                          {b.lifted_from_ghost && (
                            <span className="text-[9px] text-[#4A5D4E] uppercase tracking-[0.15em] ml-auto">Lifted ✓</span>
                          )}
                        </li>
                      ))}
                      {locks.map((b, i) => (
                        <li key={`locked-${slug}-${i}`} className="text-sm text-[#6B6A68] flex items-center gap-2 italic opacity-70" data-testid={`bookmark-locked-${slug}-${i}`}>
                          <LockKey size={12} weight="duotone" className="text-[#6B6A68] shrink-0" />
                          <span className="truncate line-through decoration-[#6B6A68]/40">{b.file_title || "Untitled"}</span>
                          <span className="text-[9px] text-[#C16E5A] uppercase tracking-[0.15em] ml-auto">Locked</span>
                        </li>
                      ))}
                    </ul>
                    {locks.length > 0 && (
                      <button
                        onClick={upgradeToCreator}
                        disabled={upgrading}
                        data-testid={`bookmark-unlock-${slug}`}
                        className="mt-3 text-[11px] text-[#C16E5A] hover:text-[#A85E4A] underline"
                      >
                        Unlock all {locks.length} locked bookmark{locks.length === 1 ? "" : "s"} — upgrade to Creator
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </section>
        )}

        {/* iter-35e: Discover section — re-positioned 3rd-from-bottom in the
            Feb 7, 2026 sprint. Never let the Fan Portal be a dead end.
            Feb 18, 2026 — collapsed by default; opens to reveal the unified
            search bar (People + Silos) PLUS the existing recommended cards
            beneath. */}
        <section className="mt-12" data-testid="fan-interests-section">
          <button
            type="button"
            onClick={() => setInterestsOpen((v) => !v)}
            data-testid="fan-interests-toggle"
            className="w-full flex items-center justify-between gap-3 text-left group"
            aria-expanded={interestsOpen}
          >
            <div className="min-w-0">
              <h2 className="font-serif-display text-2xl text-[#1C1B1A] silo-loose">Define your interests</h2>
              {!interestsOpen && (
                <p className="text-xs text-[#6B6A68] mt-1 silo-loose">
                  Pick a few topics so we can suggest silos worth your time.
                </p>
              )}
            </div>
            <CaretDown
              size={18}
              weight="duotone"
              className={`text-[#6B6A68] shrink-0 transition-transform ${interestsOpen ? "rotate-180" : ""} group-hover:text-[#1C1B1A]`}
            />
          </button>
          {interestsOpen && (
            <div className="mt-4" data-testid="fan-interests-body">
              <InterestsPicker onCount={setInterestCount} />
            </div>
          )}
        </section>

        <section className="mt-12" data-testid="fan-discover-section">
          <button
            type="button"
            onClick={() => setDiscoverOpen((v) => !v)}
            data-testid="fan-discover-toggle"
            className="w-full flex items-center justify-between gap-3 text-left group"
            aria-expanded={discoverOpen}
          >
            <div className="min-w-0">
              <h2 className="font-serif-display text-2xl text-[#1C1B1A] silo-loose">Discover more silos</h2>
              {!discoverOpen && (
                <p className="text-xs text-[#6B6A68] mt-1 silo-loose">
                  Search by name, handle, or topic — or browse what's open below.
                </p>
              )}
            </div>
            <CaretDown
              size={18}
              weight="duotone"
              className={`text-[#6B6A68] shrink-0 transition-transform ${discoverOpen ? "rotate-180" : ""} group-hover:text-[#1C1B1A]`}
            />
          </button>
          {discoverOpen && (
            <div className="mt-5 space-y-6" data-testid="fan-discover-body">
              {/* Oct 2026 (Randy) — *"we need to provide a toggle just below the
                  Discover more silos title… with this ON the discover more
                  silos search only pull up results that match the users
                  defined interests."* Off until he flips it, then remembered.
                  People are carried by their silos: with the filter on, a
                  creator appears only when one of THEIR silos matched. */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  type="button"
                  role="switch"
                  aria-checked={interestsOnly}
                  disabled={!interestCount}
                  onClick={() => setInterestsOnly((v) => !v)}
                  data-testid="discover-interests-only-toggle"
                  className="flex items-center gap-2.5 text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <span className={`w-9 h-5 rounded-full shrink-0 transition-colors relative ${interestsOnly ? "bg-[#4A5D4E]" : "bg-[#D9D7D1]"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-[left] ${interestsOnly ? "left-[18px]" : "left-0.5"}`} />
                  </span>
                  <span className="text-xs text-[#1C1B1A] silo-loose group-hover:text-[#4A5D4E]">
                    Show only Defined interests
                  </span>
                </button>
                <span className="text-[11px] text-[#6B6A68]" data-testid="discover-interests-only-note">
                  {!interestCount
                    ? "Pick a few interests above to use this."
                    : interestsOnly
                      ? `Searching your ${interestCount} interest${interestCount === 1 ? "" : "s"} only.`
                      : "Searching the whole platform."}
                </span>
              </div>

              {/* Unified search — People + Silos, match-quality only */}
              <SearchBar variant="inline" interestsOnly={interestsOnly} placeholder="Search a creator's name, handle, or topic…" />

              {(data?.discover_silos || []).filter((s) => !!s.silo_slug).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] mb-3 silo-loose">
                    A few open silos to peek at
                  </div>
                  {/* Feb 21, 2026 — single-column compact cards (was
                      2-col big cards). Backend cap reduced to 6 (was 12).
                      Removed "by Creator" subline. */}
                  <div className="space-y-2">
                    {(data.discover_silos || []).filter((s) => !!s.silo_slug).map((s) => (
                      <Link key={s.silo_id} to={`/s/${s.silo_slug}`}
                        className="bg-white border hairline rounded-lg p-3 block hover:border-[#4A5D4E] hover:shadow-sm transition-colors flex items-center gap-3"
                        data-testid={`discover-card-${s.silo_slug}`}>
                        {s.silo_art_enabled && s.cube?.faces?.center?.image_id ? (
                          <SiloArt cube={s.cube} enabled className="w-10 h-10 shrink-0" data-testid={`discover-art-${s.silo_slug}`} />
                        ) : (
                          // Oct 2026 (Randy) — every row carries art now, even
                          // a silo that never set a cover: a lettered tile the
                          // same size keeps the column straight without making
                          // the row any taller than it already was.
                          <div
                            className="w-10 h-10 shrink-0 rounded-xl bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center font-serif-display text-base"
                            data-testid={`discover-art-fallback-${s.silo_slug}`}
                          >
                            {(s.silo_name || "?").trim().charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif-display text-base text-[#1C1B1A] truncate">{s.silo_name}</h3>
                          {s.silo_tagline && <p className="text-[11px] text-[#6B6A68] line-clamp-1">{s.silo_tagline}</p>}
                        </div>
                        {s.price_cents > 0 && (
                          <span className="text-[10px] text-[#6B6A68] tabular-nums shrink-0">{fmt$(s.supporter_total_cents || s.price_cents)}/mo</span>
                        )}
                        <span className="text-[11px] px-2.5 py-1.5 rounded-md border hairline text-[#4A5D4E] inline-flex items-center gap-1.5 shrink-0">
                          Visit <ArrowSquareOut size={11} />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Feb 2026 (Randy) — the bottom "Become a Creator" CTA is REMOVED
            globally. Only the top two CTA pills stay (aesthetic-polished).
            The DFY card below is HIDDEN for family collaborators on
            organizer silos (they don't need to be pitched a $700 upgrade
            when they're already inside a shared family surface); still
            shows on all other silo contexts.
            Feb 19, 2026 (Randy Tactic A) — only when the org silo is
            actually family-shaped. A colleague on a team-shaped organizer
            silo still sees the DFY pitch. */}

        {/* COMPACT DFY ADVERT — very bottom of the portal (just above footer).
            ~50% footprint via the `compact` prop. Hidden for operators AND
            for users who collaborate on at least one *family-shaped*
            organizer silo. */}
        {!viewer.isOperator && !familySilos.some((s) => s.silo_kind === "organizer" && s.is_family_shaped) && (
          <div className="mt-10" data-testid="fan-portal-dfy-card">
            <DfyPurchaseCard
              compact
              referrerCode={new URLSearchParams(window.location.search).get("ref")}
            />
          </div>
        )}

        <footer className="mt-12 text-center text-xs text-[#6B6A68] silo-loose">
          Not seeing a subscription you purchased? It may still be processing from Stripe — try reloading in a minute.
        </footer>
      </main>

      {/* Feb 2026 pt89h (Randy) — Pending chore-confirmation modal for
          parent-pill collaborators (e.g. Randy's wife) who don't have
          Creator Dashboard access. */}
      {familyPendingOpen && (
        <PendingConfirmationsModal
          siloId={familyPendingOpen.id} siloName={familyPendingOpen.name}
          onClose={() => setFamilyPendingOpen(null)}
          onCountChanged={(n) => setFamilyPendingCounts((c) => ({ ...c, [familyPendingOpen.id]: n }))} />
      )}

      {/* Welcome modal — shows once per session.
          Oct 2026 (Randy, fan-first pass) — this used to open with a lock icon
          and a big coral "Start my 14 days", which told somebody who had just
          joined FREE that the real product was behind a paid door. It now
          leads with what their fan account actually does, and the trial is a
          quiet line at the bottom for the people who want more. */}
      {showLockdown && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" data-testid="lockdown-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="modal-safe bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
          >
            <div className="w-12 h-12 rounded-full bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mx-auto">
              <Books size={22} weight="duotone" />
            </div>
            <h2 className="font-serif-display text-2xl text-[#1C1B1A] mt-4 text-center">
              {featuredCreator ? <>You're subscribed to <em className="text-[#4A5D4E]">{featuredCreator}</em>.</> : <>Welcome to My Silo.</>}
            </h2>
            <p className="text-sm text-[#6B6A68] mt-3 text-center leading-relaxed">
              This is your fan portal, and it's free for as long as you like. Browse public silos —
              digital personas, YouTube digital twins, experts who've published their corpus — ask
              them anything, bookmark the answers, and save any silo to your home screen.
            </p>
            <div className="flex flex-col gap-2 mt-6">
              <button
                onClick={() => { setShowLockdown(false); nav("/discover"); }}
                data-testid="lockdown-explore-btn"
                className="w-full bg-[#3A4A3E] text-white hover:bg-[#1C1B1A] px-5 py-3 rounded-md flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Books size={16} weight="duotone" /> Explore silos
              </button>
              <button
                onClick={closeLockdownAndMaybeShowA2HS}
                data-testid="lockdown-close-btn"
                className="w-full text-xs text-[#6B6A68] hover:text-[#1C1B1A] py-2"
              >
                Maybe later — continue as a fan
              </button>
            </div>
            {/* The creator path, offered rather than pushed — and it opens the
                page that shows BOTH ways in (subscribe now or try it first),
                not the trial on its own. Randy, Oct 2026: "this link should
                open up the same page that says 'Your account is ready'". */}
            <p className="text-xs text-[#6B6A68] mt-4 text-center leading-relaxed border-t hairline pt-4">
              Want to build your own Digital AI Twin and earn as a creator?{" "}
              <button
                type="button"
                onClick={() => { setShowLockdown(false); nav("/start"); }}
                data-testid="lockdown-upgrade-btn"
                className="text-[#4A5D4E] underline underline-offset-2 hover:text-[#1C1B1A] font-medium"
              >
                See how to start →
              </button>
            </p>
          </motion.div>
        </div>
      )}

      <TrialStartModal open={showTrialStart} onOpenChange={setShowTrialStart} />

      {/* iter-33: Add-to-Home-Screen popup. Mobile only. Shows after first
          signup (post-lockdown close) AND on second visit if not dismissed. */}      {showA2HS && platformInstall !== "desktop" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" data-testid="a2hs-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="modal-safe bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
          >
            <button onClick={() => dismissA2HS(false)} className="absolute top-3 right-3 text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="a2hs-close-x">
              <X size={16} />
            </button>
            <div className="w-12 h-12 rounded-full bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center mx-auto">
              <DeviceMobile size={22} weight="duotone" />
            </div>
            <h2 className="font-serif-display text-xl text-[#1C1B1A] mt-3 text-center">
              Add My Silo to your home screen
            </h2>
            <p className="text-sm text-[#6B6A68] mt-2 text-center leading-relaxed">
              One tap from your home screen — like a real app. No app-store download.
            </p>

            {platformInstall === "ios" && (
              <ol className="text-sm text-[#1C1B1A] mt-5 space-y-2.5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid="a2hs-ios-steps">
                <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">1.</span> Tap the <strong>Share icon</strong> at the bottom of Safari (square with an up-arrow).</li>
                <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">2.</span> Scroll down and tap <strong>"Add to Home Screen"</strong>.</li>
                <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">3.</span> Tap <strong>Add</strong> in the top-right corner.</li>
              </ol>
            )}
            {platformInstall === "android" && (
              <ol className="text-sm text-[#1C1B1A] mt-5 space-y-2.5 bg-[#FDFCF8] border hairline rounded-md p-4" data-testid="a2hs-android-steps">
                <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">1.</span> Tap the <strong>three-dot menu</strong> in the top-right of Chrome.</li>
                <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">2.</span> Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>.</li>
                <li className="flex gap-2"><span className="text-[#4A5D4E] font-medium shrink-0">3.</span> Confirm by tapping <strong>Install</strong> / <strong>Add</strong>.</li>
              </ol>
            )}

            <div className="flex flex-col gap-2 mt-5">
              <button
                onClick={() => dismissA2HS(false)}
                data-testid="a2hs-later-btn"
                className="w-full text-sm bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] py-2.5 rounded-md"
              >
                Got it — I'll do this now
              </button>
              <button
                onClick={() => dismissA2HS(true)}
                data-testid="a2hs-never-btn"
                className="w-full text-xs text-[#6B6A68] hover:text-[#C16E5A] py-1.5"
              >
                Don't show this again
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Upgrade pitch popup — Creator OR Affiliate Creator. Each tier has its
          own copy + Stripe path. Affiliate-tier is gated to fans with at least
          some referral activity (we don't pitch the $300 plan blindly), but for
          now we just show both and trust fans to self-select. */}
      {upgradeOpen && (
        <UpgradePitchModal
          tier={upgradeOpen}
          pricing={pricing}
          /* Nov 2026 (Randy) — mirrors the server's `needs_monthly`: anyone who
             isn't already paying (or comped, or mid-trial) gets the monthly
             bundled into the same Stripe session, so the modal must quote the
             combined total rather than the Pro amount alone. */
          needsMonthly={!(
            user?.is_paid_creator ||
            ["active", "trialing"].includes(user?.platform_subscription_status) ||
            user?.is_trialing
          )}
          upgrading={upgrading}
          onClose={() => setUpgradeOpen(null)}
          onConfirm={async () => {
            if (upgrading) return;
            setUpgrading(true);
            try {
              if (upgradeOpen === "creator") {
                const upgrade_source_silo_slug = (data?.subscriptions || [])[0]?.silo_slug;
                const r = await api.post("/stripe/platform/checkout", {
                  origin_url: window.location.origin,
                  return_path: "/fan-portal",
                  ...(upgrade_source_silo_slug ? { upgrade_source_silo_slug } : {}),
                });
                window.location.href = r.data.url;
              } else if (upgradeOpen === "affiliate") {
                const r = await api.post("/affiliate/start-checkout", {
                  origin_url: window.location.origin,
                });
                window.location.href = r.data.url;
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || "Could not start checkout.");
              setUpgrading(false);
            }
          }}
        />
      )}
    </div>
  );
}

function UpgradePitchModal({ tier, upgrading, onClose, onConfirm, pricing, needsMonthly = false }) {
  const isCreator = tier === "creator";
  const [buffetOpen, setBuffetOpen] = useState(false);
  const creatorMonthly = fmt$(pricing?.creator_monthly_fee_cents);
  const affiliateGate = fmt$(pricing?.day_one_bundle_discount_threshold_cents);
  // Nov 3 2026 (Randy) — someone who isn't paying the monthly yet now buys both
  // in ONE checkout, so the price on the button has to be the REAL total they
  // are about to be charged. Quoting "$333 one-time" and then showing $348 in
  // Stripe is exactly the kind of surprise that kills a sale at the card field.
  const comboTotal = needsMonthly
    ? fmt$((pricing?.day_one_bundle_discount_threshold_cents || 0) + (pricing?.creator_monthly_fee_cents || 0))
    : affiliateGate;
  // Feb 2026 (Randy pt-silo-caps-dynamic) — the two silo-cap numbers used
  // to be hardcoded here (3 and "more silos"). Now both flow from
  // /public/pricing → the same constants that gate silo creation on the
  // server. Change one place, every marketing copy updates.
  const creatorSiloCap = pricing?.creator_tier_silo_cap || 3;
  const proSiloCap = pricing?.creator_pro_tier_silo_cap || 12;
  // Oct 7 2026 (Randy) — both pitches rewritten OUTCOME-FIRST. They previously
  // listed tool NAMES ("Macros — automate YouTube ingestion end-to-end"), which
  // means nothing to someone who has never ingested anything, and the single
  // strongest differentiator — the AI can only speak from the creator's own
  // corpus — was buried as half of bullet one. It is now the subtitle.
  //
  // DELIBERATELY ABSENT: any referral-earnings claim. Randy's Jul 3 2026 FTC
  // income de-risk decision governs this surface — product-first, Partner
  // Program stays a quiet note with the disclaimer, and the earnings simulator
  // was deleted as the top risk. An "earn a side income" bullet here would
  // reverse that, so it is not here.
  //
  // Seven bullets is the ceiling: past that people stop reading, and the
  // "Explore & customize the full toolkit →" link below already carries the
  // long inventory.
  const copy = isCreator
    ? {
        title: "Become a Creator",
        sub: "An AI that answers only from what you've written — never from the open internet.",
        bullets: [
          "Feed it what you have already made — books, transcripts, sermons, notes. It answers from your corpus, or it tells the visitor it doesn't know.",
          "It sounds like you, not like a chatbot. The Voice Kit learns your phrasing from samples you pick.",
          `Build up to ${creatorSiloCap} branded silo${creatorSiloCap === 1 ? "" : "s"}, each with its own audience and its own voice.`,
          "Open it to the world free, or charge for it — fan subscriptions and tipping are already built in.",
          `${creatorMonthly}/month. Bring your own Gemini key — the free tier is enough to start.`,
        ],
        cta: upgrading ? "Opening Stripe…" : `Start as Creator — ${creatorMonthly}/mo`,
        accent: "#4A4A48",
      }
    : {
        title: "Unlock Creator Pro",
        sub: "Your twin on any site you own, on YouTube, and in your house.",
        // Nov 2026 (Randy) — *"it's too focused on youtube only perks… just be
        // more broad."* Reordered so the breadth lands first: more silos, then
        // the widget anyone with a website can use, and YouTube as one strong
        // example rather than the whole pitch.
        bullets: [
          "Everything in Creator — your silos, your own API key, fan subscriptions, tipping.",
          `${creatorSiloCap} silos becomes ${proSiloCap} — one per project, client, or side of your life, each with its own voice and its own corpus.`,
          "Put a twin on any site you own: an embeddable widget that answers in the corner of your own page, from your material only.",
          "Build from what you already have — the Workbench and Refinery turn rough notes, recordings and transcripts into finished silo material.",
          "Turn a YouTube channel into a twin: point it at your videos, the built-in tools handle the ingestion, and it can answer your comment section in your voice.",
          "Run your household or your team with it — a family organiser, a chore chart your kids actually track on, and a coach that remembers what matters to the people you love.",
          "Plus Boost for deeper answers, Silo Wars, Collaboration and the AI Assistant.",
          `${affiliateGate} once, and your ${creatorMonthly}/mo Creator subscription starts with it — ${comboTotal} today, then ${creatorMonthly}/month. No recurring Pro fee.`,
        ],
        cta: upgrading ? "Opening Stripe…" : `Unlock Creator Pro — ${comboTotal} today`,
        accent: "#A89360",
      };
  return (
    <>
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`fan-portal-upgrade-modal-${tier}`}>
      <div className="modal-safe bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-[0.22em]" style={{ color: copy.accent }}>
          {isCreator ? <Crown size={12} weight="duotone" /> : <Sparkle size={12} weight="duotone" />} {isCreator ? "Creator tier" : "Creator Pro tier"}
        </div>
        <h2 className="font-serif-display text-2xl text-[#1C1B1A] mb-1">{copy.title}</h2>
        <p className="text-sm text-[#6B6A68] mb-4">{copy.sub}</p>
        <ul className="space-y-2 text-sm text-[#1C1B1A] leading-relaxed mb-5">
          {copy.bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[#4A5D4E] shrink-0">✓</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {!isCreator && (
          <button
            type="button"
            onClick={() => setBuffetOpen(true)}
            data-testid="fan-portal-explore-toolkit"
            className="w-full mb-4 text-sm font-medium underline underline-offset-4 text-left"
            style={{ color: copy.accent }}
          >
            Explore &amp; customize the full toolkit →
          </button>
        )}
        {!isCreator && (
          <div className="mb-4 pt-3 border-t hairline" data-testid="fan-portal-partner-note">
            <p className="text-[11px] text-[#6B6A68] mb-1.5">
              <PartnerProgramNote testId="fan-portal-partner-program-note" />
            </p>
            <EarningsDisclaimer variant="short" />
          </div>
        )}
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={upgrading}
            className="text-sm text-[#6B6A68] hover:text-[#1C1B1A]"
            data-testid={`fan-portal-upgrade-cancel-${tier}`}
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={upgrading}
            data-testid={`fan-portal-upgrade-confirm-${tier}`}
            className="px-5 py-2.5 rounded-md text-white text-sm hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5"
            style={{ background: copy.accent }}
          >
            {copy.cta} <ArrowRight size={13} weight="duotone" />
          </button>
        </div>
        {isCreator && (
          <AutoRenewDisclosure
            priceLabel={pricing?.creator_monthly_fee_cents
              ? `${creatorMonthly}/month`
              : "the current platform subscription price"}
            className="mt-4 pt-4 border-t border-[#E4E2DC]" />
        )}
      </div>
    </div>
    <CreatorProBuffetModal open={buffetOpen} onOpenChange={setBuffetOpen} initialMode="simple" />
    </>
  );
}

// Jun 2026 — per-silo subscriber notification opt-in. Small control on each
// subscription card. Turning it on registers a silo-scoped web-push
// subscription on this device + records consent; turning it off opts out.
function SiloPingToggle({ siloId, siloSlug }) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/staging/silo-optin/${siloId}`);
        if (!cancel) setEnabled(!!r.data?.enabled);
      } catch { /* default off */ }
    })();
    return () => { cancel = true; };
  }, [siloId]);

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
      const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
      if (iOS && !standalone) {
        throw new Error("On iPhone/iPad, add My Silo to your Home Screen first (Share → Add to Home Screen), then open it from there to turn on notifications. Private browsing can't enable them.");
      }
      throw new Error("This browser can't enable notifications here. Try a normal (non-private) Chrome or Safari window.");
    }
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      throw new Error("Notifications blocked. Enable them in your browser settings.");
    }
    const { data: keyResp } = await api.get("/staging/push/vapid-public-key");
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
    });
    await api.post("/staging/push/subscribe", {
      endpoint: sub.endpoint,
      keys: { p256dh: arrayBufferToBase64(sub.getKey("p256dh")), auth: arrayBufferToBase64(sub.getKey("auth")) },
      user_agent: navigator.userAgent,
      silo_slug: siloSlug || null,
    });
    await api.post(`/staging/silo-optin/${siloId}`, { enabled: true });
  };

  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (!enabled) {
        await enablePush();
        setEnabled(true);
        toast.success("Notifications on for this silo.");
      } else {
        await api.post(`/staging/silo-optin/${siloId}`, { enabled: false });
        setEnabled(false);
        toast("Notifications off for this silo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Couldn't update notifications.");
    } finally { setBusy(false); }
  };

  return (
    <span className="relative inline-flex items-center gap-1">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={`sub-notif-toggle-${siloSlug}`}
        title={enabled ? "Notifications on — tap to turn off" : "Get notified when this creator sends a ping"}
        className={`text-[10px] inline-flex items-center gap-1 underline-offset-2 hover:underline disabled:opacity-50 ${enabled ? "text-[#4A5D4E]" : "text-[#6B6A68] hover:text-[#4A5D4E]"}`}
      >
        {enabled ? <Bell size={12} weight="fill" /> : <BellSlash size={12} weight="duotone" />}
        {enabled ? "notifications on" : "allow notifications"}
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTip((v) => !v); }}
        onBlur={() => setTimeout(() => setShowTip(false), 200)}
        data-testid={`sub-notif-info-${siloSlug}`}
        className="text-[#4A5D4E] hover:text-[#1C1B1A]"
        title="What's this?"
      >
        <Eye size={11} weight="duotone" />
      </button>
      {showTip && (
        <span className="absolute bottom-full right-0 mb-1 z-50 w-[260px] bg-white border hairline rounded-md shadow-lg p-3 text-[11px] leading-relaxed text-[#1C1B1A] normal-case text-left" data-testid={`sub-notif-tip-${siloSlug}`}>
          <strong>What&apos;s this?</strong> Get a phone alert when this creator sends a scheduled question or nudge for this silo. It&apos;s per-silo — turn it on only where you want it.
          <span className="block mt-1.5 text-[#6B6A68]">Creator? Set these up in your silo&apos;s <em>Staging</em> tab → New ping → check &quot;Also send to subscribers.&quot;</span>
        </span>
      )}
    </span>
  );
}

// Web-push helpers (shared shape with StagingTab).
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
}
